import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { useSimpleToast, getErrorMessage } from '../components/Toast';

const API = process.env.REACT_APP_BACKEND_URL;

function OffsiteAttendance({ user, onLogout }) {
  const { showToast, ToastComponent } = useSimpleToast();
  const [attendance, setAttendance] = useState([]);
  const [todayStatus, setTodayStatus] = useState(null);
  const [showCheckInForm, setShowCheckInForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('check-in');
  
  const [checkInData, setCheckInData] = useState({
    location_name: '',
    address: '',
    latitude: 0,
    longitude: 0,
    purpose: '',
    travel_mode: 'Own Vehicle',
    start_odometer: '',
    selfie_url: '',
    odometer_image_url: ''
  });

  const [checkOutData, setCheckOutData] = useState({
    end_odometer: '',
    remarks: '',
    checkout_odometer_image_url: ''
  });

  // Camera states
  const [showCamera, setShowCamera] = useState(false);
  const [cameraType, setCameraType] = useState(''); // 'selfie', 'odometer_checkin', 'odometer_checkout'
  const [capturedImage, setCapturedImage] = useState(null);
  const [stream, setStream] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  
  // Location tracking states
  const [isTracking, setIsTracking] = useState(false);
  const [trackingInterval, setTrackingInterval] = useState(null);

  const videoRef = useRef(null);

  useEffect(() => {
    fetchTodayStatus();
    fetchMyAttendance();
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
      if (trackingInterval) {
        clearInterval(trackingInterval);
      }
    };
  }, [stream, trackingInterval]);

  // Set video stream when camera opens
  useEffect(() => {
    if (stream && videoRef.current) {
      videoRef.current.srcObject = stream;
    }
  }, [stream]);

  const fetchTodayStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/offsite-attendance/today-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTodayStatus(response.data);
    } catch (error) {
      console.error('Error fetching today status:', error);
    }
  };

  const fetchMyAttendance = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/offsite-attendance/my-attendance`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAttendance(response.data.attendance || []);
    } catch (error) {
      console.error('Error fetching attendance:', error);
    }
  };

  // Location functions
  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setCheckInData({
            ...checkInData,
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          });
          showToast('Location captured successfully!', 'success');
        },
        (error) => {
          showToast('Error getting location: ' + error.message, 'error');
        },
        { enableHighAccuracy: true, timeout: 5000, maximumAge: 0 }
      );
    } else {
      showToast('Geolocation is not supported by this browser.', 'error');
    }
  };

  // Auto-tracking location
  const startLocationTracking = () => {
    if (!navigator.geolocation) {
      showToast('Geolocation is not supported by this browser.', 'error');
      return;
    }

    setIsTracking(true);
    getCurrentLocation(); // Get initial location

    // Update location every 5 minutes
    const interval = setInterval(() => {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setCheckInData(prev => ({
            ...prev,
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          }));
          console.log('Location updated:', position.coords.latitude, position.coords.longitude);
        },
        (error) => {
          console.error('Error tracking location:', error);
        },
        { enableHighAccuracy: true, timeout: 5000, maximumAge: 0 }
      );
    }, 5 * 60 * 1000); // 5 minutes

    setTrackingInterval(interval);
  };

  const stopLocationTracking = () => {
    if (trackingInterval) {
      clearInterval(trackingInterval);
      setTrackingInterval(null);
    }
    setIsTracking(false);
  };

  // Camera functions
  const openCamera = async (type) => {
    setCameraType(type);
    setShowCamera(true);
    setCapturedImage(null);

    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { 
          facingMode: type === 'selfie' ? 'user' : 'environment',
          width: { ideal: 1280 },
          height: { ideal: 720 }
        }
      });
      setStream(mediaStream);
    } catch (error) {
      alert('Error accessing camera: ' + error.message);
      setShowCamera(false);
    }
  };

  const capturePhoto = () => {
    const video = videoRef.current;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const context = canvas.getContext('2d');
    context.drawImage(video, 0, 0);
    
    canvas.toBlob(async (blob) => {
      const imageUrl = URL.createObjectURL(blob);
      setCapturedImage(imageUrl);
      
      // Upload image
      await uploadCapturedImage(blob);
    }, 'image/jpeg', 0.8);
  };

  const uploadCapturedImage = async (blob) => {
    setUploadingImage(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', blob, `${cameraType}_${Date.now()}.jpg`);

      const response = await axios.post(
        `${API}/api/file-uploads/attendance-photo`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      const imageUrl = response.data.file_url;

      // Update appropriate state based on camera type
      if (cameraType === 'selfie') {
        setCheckInData(prev => ({ ...prev, selfie_url: imageUrl }));
      } else if (cameraType === 'odometer_checkin') {
        setCheckInData(prev => ({ ...prev, odometer_image_url: imageUrl }));
      } else if (cameraType === 'odometer_checkout') {
        setCheckOutData(prev => ({ ...prev, checkout_odometer_image_url: imageUrl }));
      }

      showToast('Photo captured successfully!', 'success');
      closeCamera();
    } catch (error) {
      console.error('Upload error:', error);
      showToast(getErrorMessage(error), 'error');
      setCapturedImage(null);
    } finally {
      setUploadingImage(false);
    }
  };

  const retakePhoto = () => {
    setCapturedImage(null);
  };

  const closeCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    setShowCamera(false);
    setCapturedImage(null);
  };

  const handleCheckIn = async (e) => {
    e.preventDefault();
    
    if (!checkInData.selfie_url) {
      showToast('Please take a selfie before checking in!', 'warning');
      return;
    }

    setLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/api/offsite-attendance/check-in`,
        checkInData,
        { headers: { Authorization: `Bearer ${token}` }}
      );
      showToast('Checked in successfully!', 'success');
      setShowCheckInForm(false);
      fetchTodayStatus();
      fetchMyAttendance();
      resetCheckInForm();
    } catch (error) {
      console.error('Check-in error:', error);
      showToast(getErrorMessage(error), 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCheckOut = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/api/offsite-attendance/check-out`,
        checkOutData,
        { headers: { Authorization: `Bearer ${token}` }}
      );
      showToast('Checked out successfully!', 'success');
      fetchTodayStatus();
      fetchMyAttendance();
      setCheckOutData({ end_odometer: '', remarks: '', checkout_odometer_image_url: '' });
    } catch (error) {
      console.error('Check-out error:', error);
      showToast(getErrorMessage(error), 'error');
    } finally {
      setLoading(false);
    }
  };

  const resetCheckInForm = () => {
    setCheckInData({
      location_name: '',
      address: '',
      latitude: 0,
      longitude: 0,
      purpose: '',
      travel_mode: 'Own Vehicle',
      start_odometer: '',
      selfie_url: '',
      odometer_image_url: ''
    });
    stopLocationTracking();
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <ToastComponent />
      <div className="p-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Offsite/Field Attendance</h1>
          <p className="mt-2 text-gray-600">Track your attendance with selfie verification and GPS location</p>
        </div>

        {/* Today's Status Card */}
        <div className="bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg shadow-lg p-6 mb-6 text-white">
          <h2 className="text-2xl font-bold mb-4">Today's Status</h2>
          {todayStatus?.has_checked_in ? (
            <div>
              <p className="text-lg mb-2">✅ Checked In</p>
              {todayStatus?.current_location && (
                <div className="bg-white bg-opacity-20 rounded-lg p-4 mt-3">
                  <p className="font-semibold">Current Location: {todayStatus.current_location.location_name}</p>
                  <p className="text-sm mt-1">{todayStatus.current_location.address}</p>
                  <p className="text-sm mt-1">Purpose: {todayStatus.current_location.purpose}</p>
                  <p className="text-sm mt-1">Check-in Time: {new Date(todayStatus.current_location.check_in_time).toLocaleTimeString()}</p>
                </div>
              )}
              {!todayStatus?.has_checked_out && (
                <button
                  onClick={() => setActiveTab('check-out')}
                  className="mt-4 px-6 py-2 bg-white text-purple-600 rounded-lg hover:bg-gray-100 transition"
                >
                  Check Out Now
                </button>
              )}
              {todayStatus?.has_checked_out && (
                <p className="mt-3 text-green-200">✅ Checked Out</p>
              )}
            </div>
          ) : (
            <div>
              <p className="text-lg mb-4">Not checked in yet today</p>
              <button
                onClick={() => setShowCheckInForm(true)}
                className="px-6 py-3 bg-white text-purple-600 rounded-lg hover:bg-gray-100 transition font-semibold"
              >
                📍 Check In Now
              </button>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="flex gap-4 mb-6">
          <button
            onClick={() => setActiveTab('check-in')}
            className={`px-6 py-2 rounded-lg transition ${
              activeTab === 'check-in'
                ? 'bg-[#2b3380] text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Check-In
          </button>
          <button
            onClick={() => setActiveTab('check-out')}
            className={`px-6 py-2 rounded-lg transition ${
              activeTab === 'check-out'
                ? 'bg-[#2b3380] text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
            disabled={!todayStatus?.has_checked_in || todayStatus?.has_checked_out}
          >
            Check-Out
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`px-6 py-2 rounded-lg transition ${
              activeTab === 'history'
                ? 'bg-[#2b3380] text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            History
          </button>
        </div>

        {/* Check-In Tab */}
        {activeTab === 'check-in' && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-bold mb-4">Check In at Location</h2>
            <button
              onClick={() => setShowCheckInForm(true)}
              className="px-6 py-3 bg-[#2b3380] text-white rounded-lg hover:bg-[#1f2660] transition"
            >
              + New Check-In
            </button>
          </div>
        )}

        {/* Check-Out Tab */}
        {activeTab === 'check-out' && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-bold mb-4">Check Out</h2>
            <form onSubmit={handleCheckOut} className="space-y-4 max-w-lg">
              {/* Odometer Image for Checkout */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">End Odometer Photo (optional)</label>
                {checkOutData.checkout_odometer_image_url ? (
                  <div className="relative">
                    <img src={checkOutData.checkout_odometer_image_url} alt="Odometer" className="w-full h-48 object-cover rounded-lg" />
                    <button
                      type="button"
                      onClick={() => openCamera('odometer_checkout')}
                      className="absolute top-2 right-2 px-3 py-1 bg-white text-[#2b3380] rounded-lg shadow-lg hover:bg-gray-100"
                    >
                      Retake
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => openCamera('odometer_checkout')}
                    className="w-full px-4 py-6 border-2 border-dashed border-gray-300 rounded-lg hover:border-[#2b3380] transition text-center"
                  >
                    <div className="text-4xl mb-1">📷</div>
                    <p className="text-sm font-medium text-gray-700">Click to capture end odometer</p>
                  </button>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">End Odometer Reading (optional)</label>
                <input
                  type="number"
                  step="0.1"
                  value={checkOutData.end_odometer}
                  onChange={(e) => setCheckOutData({...checkOutData, end_odometer: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2b3380]"
                  placeholder="Enter odometer reading"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Remarks (optional)</label>
                <textarea
                  value={checkOutData.remarks}
                  onChange={(e) => setCheckOutData({...checkOutData, remarks: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2b3380]"
                  rows="3"
                  placeholder="Any additional notes"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition disabled:bg-gray-400"
              >
                {loading ? 'Checking Out...' : 'Check Out'}
              </button>
            </form>
          </div>
        )}

        {/* History Tab */}
        {activeTab === 'history' && (
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="p-4 border-b">
              <h2 className="text-xl font-bold">Attendance History</h2>
            </div>
            {attendance.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <p>No offsite attendance records found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Locations</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Travel Mode</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Distance (km)</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {attendance.map((record) => (
                      <tr key={record.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          {new Date(record.date).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4">
                          {record.locations?.map((loc, idx) => (
                            <div key={idx} className="text-sm mb-2">
                              <p className="font-medium">{loc.location_name}</p>
                              <p className="text-gray-500 text-xs">{loc.address}</p>
                              <p className="text-gray-500 text-xs">Purpose: {loc.purpose}</p>
                            </div>
                          ))}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          {record.travel_mode || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          {record.total_distance_km ? record.total_distance_km.toFixed(2) : 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            {record.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Check-In Modal */}
        {showCheckInForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto my-8">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold text-gray-900">Check In at Location</h2>
                  <button
                    onClick={() => {
                      setShowCheckInForm(false);
                      resetCheckInForm();
                    }}
                    className="text-gray-500 hover:text-gray-700 text-2xl"
                  >
                    ×
                  </button>
                </div>

                <form onSubmit={handleCheckIn} className="space-y-4">
                  {/* Selfie Capture - MANDATORY */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Take Selfie at Location * <span className="text-red-500">(Required)</span>
                    </label>
                    {checkInData.selfie_url ? (
                      <div className="relative">
                        <img src={checkInData.selfie_url} alt="Selfie" className="w-full h-48 object-cover rounded-lg" />
                        <button
                          type="button"
                          onClick={() => openCamera('selfie')}
                          className="absolute top-2 right-2 px-3 py-1 bg-white text-[#2b3380] rounded-lg shadow-lg hover:bg-gray-100"
                        >
                          Retake
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => openCamera('selfie')}
                        className="w-full px-4 py-8 border-2 border-dashed border-red-300 rounded-lg hover:border-[#2b3380] transition text-center"
                      >
                        <div className="text-5xl mb-2">🤳</div>
                        <p className="text-sm font-medium text-gray-700">Click to take selfie</p>
                        <p className="text-xs text-red-600 mt-1">Required for check-in</p>
                      </button>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Location Name *</label>
                    <input
                      type="text"
                      required
                      value={checkInData.location_name}
                      onChange={(e) => setCheckInData({...checkInData, location_name: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2b3380]"
                      placeholder="Client name or site name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Address *</label>
                    <textarea
                      required
                      value={checkInData.address}
                      onChange={(e) => setCheckInData({...checkInData, address: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2b3380]"
                      rows="2"
                      placeholder="Full address"
                    />
                  </div>

                  {/* GPS Location with Auto-Tracking */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">GPS Location</label>
                    <div className="flex gap-2 mb-2">
                      <input
                        type="number"
                        step="any"
                        value={checkInData.latitude}
                        readOnly
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg bg-gray-50"
                        placeholder="Latitude"
                      />
                      <input
                        type="number"
                        step="any"
                        value={checkInData.longitude}
                        readOnly
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg bg-gray-50"
                        placeholder="Longitude"
                      />
                      <button
                        type="button"
                        onClick={getCurrentLocation}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition whitespace-nowrap"
                      >
                        📍 Get Now
                      </button>
                    </div>
                    <div className="flex gap-2">
                      {!isTracking ? (
                        <button
                          type="button"
                          onClick={startLocationTracking}
                          className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-sm"
                        >
                          🛰️ Start Auto-Tracking
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={stopLocationTracking}
                          className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition text-sm"
                        >
                          ⏸️ Stop Tracking
                        </button>
                      )}
                    </div>
                    {isTracking && (
                      <p className="text-xs text-green-600 mt-1 flex items-center">
                        <span className="animate-pulse mr-1">🟢</span>
                        Auto-tracking active (updates every 5 minutes)
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Purpose *</label>
                    <input
                      type="text"
                      required
                      value={checkInData.purpose}
                      onChange={(e) => setCheckInData({...checkInData, purpose: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2b3380]"
                      placeholder="Reason for visit"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Travel Mode</label>
                    <select
                      value={checkInData.travel_mode}
                      onChange={(e) => setCheckInData({...checkInData, travel_mode: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2b3380]"
                    >
                      <option value="Own Vehicle">Own Vehicle</option>
                      <option value="Company Vehicle">Company Vehicle</option>
                      <option value="Public Transport">Public Transport</option>
                    </select>
                  </div>

                  {/* Odometer Reading */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Start Odometer Reading (optional)</label>
                    <input
                      type="number"
                      step="0.1"
                      value={checkInData.start_odometer}
                      onChange={(e) => setCheckInData({...checkInData, start_odometer: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2b3380]"
                      placeholder="Vehicle odometer reading"
                    />
                  </div>

                  {/* Odometer Image Capture */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Odometer Photo (optional)</label>
                    {checkInData.odometer_image_url ? (
                      <div className="relative">
                        <img src={checkInData.odometer_image_url} alt="Odometer" className="w-full h-48 object-cover rounded-lg" />
                        <button
                          type="button"
                          onClick={() => openCamera('odometer_checkin')}
                          className="absolute top-2 right-2 px-3 py-1 bg-white text-[#2b3380] rounded-lg shadow-lg hover:bg-gray-100"
                        >
                          Retake
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => openCamera('odometer_checkin')}
                        className="w-full px-4 py-6 border-2 border-dashed border-gray-300 rounded-lg hover:border-[#2b3380] transition text-center"
                      >
                        <div className="text-4xl mb-1">📷</div>
                        <p className="text-sm font-medium text-gray-700">Click to capture odometer</p>
                      </button>
                    )}
                  </div>

                  <div className="flex gap-3 pt-4">
                    <button
                      type="button"
                      onClick={() => {
                        setShowCheckInForm(false);
                        resetCheckInForm();
                      }}
                      className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={loading || !checkInData.selfie_url}
                      className="flex-1 px-6 py-3 bg-[#2b3380] text-white rounded-lg hover:bg-[#1f2660] transition disabled:bg-gray-400"
                    >
                      {loading ? 'Checking In...' : 'Check In'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Camera Modal */}
        {showCamera && (
          <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50">
            <div className="w-full max-w-2xl p-4">
              <div className="bg-white rounded-lg overflow-hidden">
                <div className="p-4 bg-gray-800 text-white flex justify-between items-center">
                  <h3 className="text-lg font-semibold">
                    {cameraType === 'selfie' ? '🤳 Take Selfie' : '📷 Capture Odometer'}
                  </h3>
                  <button
                    onClick={closeCamera}
                    className="text-white hover:text-gray-300 text-2xl"
                  >
                    ×
                  </button>
                </div>

                <div className="relative bg-black">
                  {!capturedImage ? (
                    <video
                      ref={videoRef}
                      autoPlay
                      playsInline
                      className="w-full"
                      style={{ maxHeight: '60vh' }}
                    />
                  ) : (
                    <img
                      src={capturedImage}
                      alt="Captured"
                      className="w-full"
                      style={{ maxHeight: '60vh' }}
                    />
                  )}
                </div>

                <div className="p-4 flex gap-3 justify-center">
                  {!capturedImage ? (
                    <button
                      onClick={capturePhoto}
                      disabled={uploadingImage}
                      className="px-8 py-3 bg-[#2b3380] text-white rounded-full hover:bg-[#1f2660] transition text-lg font-semibold disabled:bg-gray-400"
                    >
                      📸 Capture
                    </button>
                  ) : (
                    <>
                      <button
                        onClick={retakePhoto}
                        disabled={uploadingImage}
                        className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition disabled:bg-gray-400"
                      >
                        🔄 Retake
                      </button>
                      <button
                        onClick={closeCamera}
                        disabled={uploadingImage}
                        className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition disabled:bg-gray-400"
                      >
                        {uploadingImage ? 'Uploading...' : '✓ Use This Photo'}
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default OffsiteAttendance;
